import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { getAPI } from "@/lib/api";
import { getAll, getDealstageAvgTimesByCompanyId } from "@/lib/supabase";
import { supabaseClient } from "@/lib/supabase/client";
import { useAppStore } from "@/stores";
import type { NestedCompany, NestedCurrency, NestedDeal } from "@revelate/types";
import { buildOutlookHistory } from "@revelate/calc/target-outlook";
import { getCompanyTarget } from "../lib/companies";
import { evaluateRiskForecast, getForecastTimePeriodRange } from "../lib/risk";
import { buildDealValueSegmentMap } from "../lib/dealValueSegments";
import { getDealValueInCompanyCurrency } from "../lib/currencies";
import { getGrowthEfficiencyCreatedDate, getGrowthEfficiencyProperties, type GrowthEfficiencySourceDeal } from "../state-of-sales-v2/growthEfficiencyData";
import { useStateOfSalesV2Forecast } from "../state-of-sales-v2/useForecastData";
import { getStateOfSalesV2ForecastPosition } from "../state-of-sales-v2/forecastPosition";
import { applyExpectedCloseV2Forecast } from "../expected-close-v2/localExpectedCloseForecast";
import { buildForecastDiagnosisStory } from "../forecast-diagnosis/forecastDiagnosisContract";
import { getOutlookCandidateIds, withOutlookPeriodRows } from "./view";

const filters = { accumulated: false, owners: [], period: "this_quarter" as const, pipelines: [], teams: [] };
const options = { revalidateOnFocus: false, revalidateOnReconnect: false };

// The existing general Deal loader includes owner joins and the complete CRM
// payload. History needs only these existing fields; keep this read-only
// projection local rather than changing shared transport or creating an API.
async function getOutlookHistory(companyId: string) {
  const deals: NestedDeal[] = [];
  const signal = AbortSignal.timeout(60_000);
  for (let page = 0; page < 50; page++) {
    const result = await supabaseClient.from("deals")
      .select("id,value,currency_id,deal_type,closed_at,is_won,is_lost,provider_data")
      .eq("company_id", Number(companyId)).eq("provider", "hubspot").eq("deal_type", "new_business")
      .order("id", { ascending: true }).range(page * 1000, (page + 1) * 1000 - 1).abortSignal(signal);
    if (result.error) throw result.error;
    // This column may hold JSON encoded as a string. Reuse the existing parser
    // before retaining only its two creation fields in the browser cache.
    for (const row of result.data) {
      const properties = getGrowthEfficiencyProperties(row as unknown as GrowthEfficiencySourceDeal);
      deals.push({ ...row, provider_data: { properties: {
        createdate: properties.createdate, hs_createdate: properties.hs_createdate,
      } } } as unknown as NestedDeal);
    }
    if (result.data.length < 1000) return deals;
  }
  throw new Error("History exceeds the supported transport size.");
}

export function useTargetOutlook() {
  const user = useAppStore(state => state.currentUser);
  const enabled = user?.company_id === 13;
  const model = useStateOfSalesV2Forecast({ enabled, initialFilters: filters, syncUrl: false, view: "forecast" });
  const [asOf, setAsOf] = useState(() => new Date());
  useEffect(() => {
    const initialDay = new Date().toDateString();
    const timer = window.setInterval(() => {
      const next = new Date();
      // Existing Forecast modules capture calendar boundaries at module load.
      if (next.toDateString() !== initialDay) { window.location.reload(); return; }
      // Amounts and the approved readout stay tied to the loaded snapshot.
      // Do not recompose a forecast every minute without new observations.
    }, 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const calendarDay = asOf.toDateString();
  const range = useMemo(() => getForecastTimePeriodRange(model.period, new Date(calendarDay)), [model.period, calendarDay]);
  const movementFailed = model.optionalErrorSources.includes("movement");
  const companyId = enabled ? String(user.company_id) : null;
  const history = useSWR(companyId ? ["query:targetOutlookHistoryProjection", companyId] : null,
    ([, id]) => getOutlookHistory(id), { ...options, errorRetryCount: 0 });
  useEffect(() => {
    if (history.data) setAsOf(new Date());
  }, [history.data]);
  const currencies = useSWR(companyId ? ["query:currencies", companyId] : null,
    ([, id]) => getAll("currencies", id), options);
  const pipelines = useSWR(companyId ? ["query:pipelines", companyId] : null,
    ([, id]) => getAPI(`providers/hubspot/pipelines/${id}`), options);
  const stageTimes = useSWR(companyId ? ["query:dealstageAvgTimes", companyId] : null,
    ([, id]) => getDealstageAvgTimesByCompanyId(Number(id)), options);
  const freshness = useSWR(companyId ? ["query:targetOutlookFreshness", companyId] : null, async ([, id]) => {
    const state = await supabaseClient.from("expected_close_v2_company_state")
      .select("current_run_id,status").eq("company_id", Number(id)).single();
    if (state.error) throw state.error;
    const run = await supabaseClient.from("expected_close_v2_runs")
      .select("as_of,completed_at,status").eq("company_id", Number(id)).eq("id", state.data.current_run_id).single();
    if (run.error) throw run.error;
    return run.data as { as_of: string; completed_at: string; status: string };
  }, options);

  const scoped = useMemo(() => {
    if (!enabled || model.isLoading || model.error || movementFailed || model.forecastEnrichmentLoading
      || !user?.company || !pipelines.data?.results || (model.period !== "prev_quarter" && !model.expectedCloseV2CompanyForecast)) return null;
    // Caller-side population selection only. All forecasting and fallback math
    // remains inside the same reviewed functions used by State of Sales.
    const prepared = model.filteredPreparedDeals.filter(deal => deal.deal_type === "new_business");
    const open = model.filteredEvaluatedForecastOpenDeals.filter(deal => deal.deal_type === "new_business");
    // Keep the historical V1 comparison on its original six-week-cutoff population.
    const legacyOpenIds = new Set(model.filteredPreparedMovementOpenDeals.map(deal => deal.id));
    const legacyOpen = open.filter(deal => legacyOpenIds.has(deal.id));
    const forecast = withOutlookPeriodRows(evaluateRiskForecast(prepared, user.company_id, pipelines.data.results,
      model.period, "all", "all", getCompanyTarget(user.company_id), false, model.companyCurrency,
      { includeL2Details: true, includeOpenDeals: true, movementDeals: legacyOpen, dealstageAvgTimes: stageTimes.data }), model.period);
    const rows = applyExpectedCloseV2Forecast(forecast.forecastChartData,
      model.expectedCloseV2CompanyForecast, open, false, forecast.forecastChartSelectedPeriodStart);
    const position = getStateOfSalesV2ForecastPosition(forecast, model.period, false, rows);
    const story = buildForecastDiagnosisStory({ accumulated: false, chartData: rows,
      companyCurrency: model.companyCurrency, expectedCloseV2CompanyForecast: model.expectedCloseV2CompanyForecast,
      filteredPreparedDeals: [...new Map([...prepared, ...open].map(deal => [deal.id, deal])).values()],
      forecast, period: model.period });
    const candidateIds = model.expectedCloseV2CompanyForecast
      ? getOutlookCandidateIds(model.expectedCloseV2CompanyForecast, range.start, range.end) : new Set<string>();
    const scenarioStory = buildForecastDiagnosisStory({ accumulated: false, chartData: rows,
      companyCurrency: model.companyCurrency, expectedCloseV2CompanyForecast: model.expectedCloseV2CompanyForecast,
      filteredPreparedDeals: open.filter(deal => candidateIds.has(String(deal.id))),
      forecast, period: model.period });
    return { forecast, position, story, scenarioStory, open };
  }, [range, model.period, enabled, model.isLoading, model.error, model.forecastEnrichmentLoading, model.filteredPreparedDeals,
    model.filteredEvaluatedForecastOpenDeals, model.filteredPreparedMovementOpenDeals, model.expectedCloseV2CompanyForecast, model.companyCurrency, movementFailed, pipelines.data, stageTimes.data, user]);

  const historical = useMemo(() => {
    if (!history.data || history.data.length >= 50_000 || !currencies.data || !user?.company) return null;
    const deals = history.data.filter(deal => deal.deal_type === "new_business").map(deal => ({ ...deal,
      value: getDealValueInCompanyCurrency(deal, currencies.data as NestedCurrency[], user.company as unknown as NestedCompany),
    }));
    const segments = buildDealValueSegmentMap(deals);
    return buildOutlookHistory(deals.map(deal => ({
      id: deal.id, value: Number(deal.value), createdAt: getGrowthEfficiencyCreatedDate({ ...deal, emails: undefined, meetings: undefined, activity_sync_status: undefined }),
      closedAt: deal.closed_at ? new Date(deal.closed_at) : null,
      isWon: Boolean(deal.is_won), isLost: Boolean(deal.is_lost),
      dimensions: { segment: segments.get(String(deal.id)) },
    })), asOf, range);
  }, [history.data, currencies.data, user?.company, asOf, range]);

  return { model, scoped, historical, history, freshness, asOf, range, enabled,
    retry: async () => { setAsOf(new Date()); await Promise.all([model.retry(), history.mutate(), freshness.mutate()]); } };
}
